import { useLocation } from 'react-router';
import { TAgreeToTermsPost } from '../api/types';
import API from '../api/api';
import GlobalContext from '../contexts/GlobalContext';
import { createContext, FC, useContext, useEffect, useState } from 'react';

type TTermsContext = {
    termsUiState: ETermsUiState;
    submitAgreeToTerms: () => void;
    hasAgreed: boolean;
    setHasAgreed: (agree: boolean) => void;
    redirectUrl: string;
};

export type TTermsRouterState = {
    redirectUrl: string;
};

export enum ETermsUiState {
    default,
    routerStateParsed,
    ready,
    loading,
    success,
    error,
}

const TermsContext = createContext<TTermsContext | undefined>(undefined);

export const TermsProvider: FC = (props) => {
    const { csrfToken, getCsrfToken } = useContext(GlobalContext);
    const { children } = props;
    const { state: routerState }: { state: TTermsRouterState } = useLocation();
    const [termsUiState, setTermsUiState] = useState(ETermsUiState.default);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [hasAgreed, setHasAgreed] = useState(false);

    const parseRouterState = () => {
        if (routerState && routerState.redirectUrl) {
            setRedirectUrl(routerState.redirectUrl);
        }
    };

    const getInitialData = async () => {
        await getCsrfToken();
    };

    const submitAgreeToTerms = async () => {
        setTermsUiState(ETermsUiState.loading);
        try {
            const postObject: TAgreeToTermsPost = {};
            if (redirectUrl) {
                postObject.returnUrl = redirectUrl;
            }
            const response = await API.post.agreeToTerms(postObject, csrfToken);
            setRedirectUrl(response.data.redirectUrl);
            setTermsUiState(ETermsUiState.success);
        } catch {
            setTermsUiState(ETermsUiState.error);
        }
    };

    useEffect(() => {
        switch (termsUiState) {
            case ETermsUiState.default:
                parseRouterState();
                setTermsUiState(ETermsUiState.routerStateParsed);
                break;
            case ETermsUiState.routerStateParsed:
                getInitialData().then(() => {
                    setTermsUiState(ETermsUiState.ready);
                });
                break;
            default:
                break;
        }
    }, [termsUiState]);

    return (
        <TermsContext.Provider value={{ termsUiState, submitAgreeToTerms, hasAgreed, setHasAgreed, redirectUrl }}>
            {children}
        </TermsContext.Provider>
    );
};

export default TermsContext;
